import React, { useEffect, useState } from 'react'
import { useForm, Validator } from '../../../util/useForm/useForm'

import * as Styled from './Cart.styles'
import {
  InvoiceAddressChoice,
  Form,
  PaymentMethod,
} from '../../../stories/organisms/ShoppingCart/PaymentForm/PaymentForm.types'
import { ShoppingCartItem } from '../../../stories/organisms/ShoppingCart/Items/Items'
import { validateZipCode } from '../../vacature/variabel/[id]/validations'
import { INITIAL_FORM_VALUES } from '../../../stories/organisms/ShoppingCart/PaymentForm/PaymentForm.constants'
import { basketItemsToItemsData } from '../../../util/analytics-mappers/analyticsMappers'
import { useDispatch } from 'react-redux'
import { showDialog } from '../../../state/actions/ui/ui'
import { Sizes as ModalSizes } from '../../../stories/atoms/Modal/Modal'
import { BasketItem } from '../../../schema/basket'
import { WebshopPackage } from '../../../schema/webshop-package'
import { RequestStatus } from '../../../state/reducers/webshop/webshop'
import navigateToPage from '../../../util/navigate-to-page/navigateToPage'
import { resetCart } from '../../../state/actions/cart/cart'
import { purgeBasket } from '../../../state/actions/webshop/webshop'
import ShoppingCart from '../../../stories/organisms/ShoppingCart/ShoppingCart'
import { Action, Category, placeOrderEventLabelMap } from '../../../util/analytics-data-attributes/analyticsAttributes'
import { noValidation, validateRequiredOption } from '../../../util/validate/validate'
import { InvoiceAddress } from '../../../schema/payment'
import { ButtonAttrs } from '../../../stories/molecules/NavigationButtons/NavigationButtons'
import { gtmPushItemsData } from '../../../util/google-tag-manager-pusher/GoogleTagManagerPusher'

export type CartProps = {
  cartItems: BasketItem[]
  vatTotal?: number
  onPrevious: React.MouseEventHandler<HTMLButtonElement>
  onNext: (payment: {
    paymentMethod: PaymentMethod
    paymentParams?: Object
    poNumber?: string
    invoiceAddress?: InvoiceAddress
  }) => void
  onRemoveProduct?: (id: string) => void
  status: {
    removeProduct?: {
      product?: WebshopPackage
      status: RequestStatus
    }
    getBasket?: RequestStatus
    payment: RequestStatus
  }
  flow: 'jobposting' | 'webshop'
}

export const TAX = 0.21
const conditionalValidation =
  (validationFn: Validator<string>) =>
  (value: string, formData: Form): any => {
    if (formData.invoiceAddressChoice === InvoiceAddressChoice.UseOtherAddress) {
      return validationFn(value, formData)
    }
    return noValidation()
  }

const Cart: React.FunctionComponent<CartProps> = ({
  // istanbul ignore next
  cartItems = [],
  vatTotal,
  onPrevious,
  onNext,
  onRemoveProduct,
  status,
  flow,
}) => {
  const dispatch = useDispatch()
  const [showPaymentOptions, setShowPaymentOptions] = useState(false)
  const items: ShoppingCartItem[] = cartItems.map(({ product, amount, price }) => ({
    id: product.productCode,
    count: amount,
    price,
    descriptions: [product.jobPosting?.name].concat(product.upgrades?.map(({ name }) => name)).filter(x => x),
    title: product.name || '',
    isLoading:
      status?.removeProduct?.status === 'pending' &&
      // istanbul ignore next
      product.productCode === status.removeProduct.product?.productCode,
  }))

  let analyticsAction: Action // = Action.jpfPlaceOrder
  const analyticsCategory: Category = flow === 'jobposting' ? Category.jobPostingFlow : Category.ecommerce
  const gross = items.reduce((accumulator, item) => accumulator + item.price, 0)
  let tax = TAX * gross
  if (flow === 'webshop' && vatTotal !== undefined) {
    // in webshop flow use the vat provided by backend
    tax = vatTotal
  }
  const net = gross + tax
  const taxPercentage = TAX * 100
  const isSubmitting = status?.payment === 'pending'
  const isLoading = status?.getBasket === 'pending'
  const showRemoveProductButton = onRemoveProduct && status?.payment !== 'success'

  useEffect(() => {
    // After a successful payment, only show the list of bought items
    if (status.payment === 'success') {
      setShowPaymentOptions(false)
    }
  }, [status?.payment])

  const form = useForm<Form>(INITIAL_FORM_VALUES, {
    referenceNumber: (value: string) => {
      if (value.length > 50) {
        return [false, 'Het referentienummer mag niet meer dan 50 tekens bevatten']
      }
      return [true, '']
    },
    paymentMethod: noValidation,
    paymentParams: noValidation,
    invoiceAddressChoice: noValidation,
    address: conditionalValidation(validateRequiredOption('Vul het adres in')),
    city: conditionalValidation(validateRequiredOption('Vul de plaatsnaam in')),
    postalCode: conditionalValidation(validateZipCode),
    companyName: conditionalValidation(validateRequiredOption('Vul de bedrijfsnaam in')),
    country: conditionalValidation(validateRequiredOption('Vul het land in')),
  })

  const formState = form.formState()

  React.useEffect(() => {
    // istanbul ignore next
    if (formState.paymentParams.value !== INITIAL_FORM_VALUES.paymentParams) {
      formState.paymentParams.onChange(INITIAL_FORM_VALUES.paymentParams)
    }
  }, [formState.paymentMethod.value])

  const prepareOnNext = () => {
    const formValues = form.getFormValues()

    let paymentParamsValue:
      | {
          directdebit?: string
        }
      | undefined

    onNext({
      paymentMethod: formValues.paymentMethod,
      paymentParams: paymentParamsValue,
      poNumber: formValues.referenceNumber || undefined,
      invoiceAddress:
        formValues.invoiceAddressChoice === InvoiceAddressChoice.UseOtherAddress
          ? {
              address: formValues.address,
              companyName: formValues.companyName,
              postalCode: formValues.postalCode,
              city: formValues.city,
              country: formValues.country,
            }
          : undefined,
    })
  }

  const handleSubmit = () => {
    if (form.validate()) {
      const formValues = form.getFormValues()
      if (formValues.paymentMethod && ['ideal', 'mastercard', 'visa'].includes(formValues.paymentMethod)) {
        dispatch(
          showDialog({
            title: 'Betaling afronden',
            content: 'Je wordt nu doorgestuurd naar het betalingsplatform om je betaling af te ronden.',
            size: ModalSizes.small,
            primaryAction: {
              label: 'Betaling afronden',
              onClick: prepareOnNext,
            },
          }),
        )
      } else {
        prepareOnNext()
      }
    }
  }

  const getNextButtonProps = (): ButtonAttrs => {
    if (showPaymentOptions) {
      analyticsAction = flow === 'jobposting' ? Action.jpfPlaceOrder : Action.websopPlaceOrder

      return {
        label: 'Betalen',
        onClick: handleSubmit,
        analytics: {
          action: analyticsAction,
          category: analyticsCategory,
          label: placeOrderEventLabelMap[formState.paymentMethod.value],
        },
        disabled: isSubmitting,
      }
    }

    if (status.payment === 'success') {
      return {
        label: 'Afsluiten',
        onClick: () => {
          dispatch(purgeBasket())
          dispatch(resetCart())
          navigateToPage('/webshop')
        },
      }
    }

    analyticsAction = flow === 'jobposting' ? Action.jpfCheckoutToPayment : Action.webshopCheckoutToPayment

    return {
      label: 'Betaalkeuze selecteren',
      onClick: () => {
        gtmPushItemsData(basketItemsToItemsData(cartItems, 'add_payment_info'))
        setShowPaymentOptions(true)
      },
      analytics: {
        action: analyticsAction,
        category: analyticsCategory,
      },
    }
  }

  analyticsAction = flow === 'jobposting' ? Action.jpfCheckoutChangeProduct : Action.webshopCheckoutChangeProduct

  const getPreviousButtonProps = () => {
    if (status.payment !== 'success') {
      return {
        onClick: onPrevious,
        label: 'Wijzig productkeuze',
        analytics: {
          action: analyticsAction,
          category: analyticsCategory,
        },
      }
    }
  }

  return (
    <Styled.Overlay isLoading={isLoading}>
      <ShoppingCart
        {...formState}
        showPaymentOptions={showPaymentOptions}
        items={items}
        price={{
          gross,
          net,
          tax,
          taxPercentage,
        }}
        onRemoveProduct={showRemoveProductButton && onRemoveProduct}
        next={getNextButtonProps()}
        previous={getPreviousButtonProps()}
      />
    </Styled.Overlay>
  )
}

export default Cart
